import {
  PlaceholderDataFunction,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { QueryKey } from 'utils/constants/query-keys.enum';
import {
  stakingAPI,
  StakingPool,
  UserPoolInfo,
  WalletRewardsInfo,
} from 'api/StakingAPI';
import { NetworkChainIds } from '@dapp/multi-lib/network';
import { useDebounce } from 'use-debounce';
import { ETH_AVERAGE_BLOCK_TIME } from 'utils/constants/ethConst';
import { QueryOptions } from 'utils/useQueryUtils';
import { useBlock, useBlockNumber, useReadContracts } from 'wagmi';
import { useEffect, useMemo } from 'react';
import {
  createPoolDataContractCalls,
  createUserPoolInfoContractCalls,
} from '../utils/contractCalls';
import { transformPoolDataFromContracts } from '../utils/poolDataTransformers';
import { transformUserPoolInfoFromContracts } from '../utils/userPoolDataTransformers';
import { calculateStake } from '../utils/stakingCalculations';
import { queryKeys } from 'structure/pv2/vault/configs/queryKeys';

const logContractInteractionError = (
  message: string,
  error: unknown,
  context?: Record<string, unknown>
) => {
  console.error(message, {
    error,
    ...context,
  });
};

export const stakingPoolListQueryOptions = (
  options?: QueryOptions,
  tokenSymbol?: string
) => {
  const { query } = options ?? {};

  return {
    queryKey: queryKeys.stakingPoolList({ tokenSymbol: tokenSymbol ?? '' }),
    queryFn: () => stakingAPI.loadPoolList(tokenSymbol),
    ...query,
  };
};

export const useStakingPoolList = (
  options?: QueryOptions,
  tokenSymbol?: string
) => {
  return useQuery(stakingPoolListQueryOptions(options, tokenSymbol));
};

export const useStakingPoolData = (pool: StakingPool) => {
  const chainId = pool.chainId as number;

  // Prepare all contract calls using wagmi's useContractReads
  // This automatically uses the wallet provider when available
  const contracts = useMemo(
    () => createPoolDataContractCalls(pool, chainId),
    [pool, chainId]
  );

  const {
    data: contractData,
    isLoading,
    error,
    refetch,
    queryKey: contractDataQueryKey,
    error: contractDataError,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
  } = useReadContracts({
    contracts: contracts,
    query: {
      enabled: contracts.length > 0,
    },
  });

  useEffect(() => {
    if (contractDataError) {
      logContractInteractionError(
        'Error reading staking pool contract data',
        contractDataError,
        {
          poolId: pool.id,
          chainId,
          stakingContractAddress: pool.stakingContractAddress,
        }
      );
    }
  }, [contractDataError]);

  const queryClient = useQueryClient();
  const { data: blockNumber } = useBlockNumber({
    watch: true,
    cacheTime: ETH_AVERAGE_BLOCK_TIME * 5,
  });

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: contractDataQueryKey });
  }, [queryClient, blockNumber]);

  // Transform the contract data to match StakingPool format
  const poolData: StakingPool | undefined = useMemo(() => {
    return transformPoolDataFromContracts(contractData, pool);
  }, [contractData, pool]);

  // Return in a format compatible with useQuery for consistency
  return {
    data: poolData,
    isLoading,
    isFetched: !isLoading && contractData !== undefined,
    error,
    refetch,
    isSuccess: !!poolData && !error,
    isError: !!error,
  };
};

type UseStakingUserPoolInfoParams = {
  poolContractAddress: `0x${string}`;
  walletAddress: `0x${string}`;
  chainId: NetworkChainIds;
  pool: StakingPool;
};

export const useStakingUserPoolInfo = (
  options: UseStakingUserPoolInfoParams
) => {
  const { poolContractAddress, walletAddress, chainId, pool } = options;

  // Prepare contract calls using wagmi's useContractReads
  // This automatically uses the wallet provider when available
  const contracts = useMemo(
    () =>
      createUserPoolInfoContractCalls(
        poolContractAddress,
        walletAddress,
        chainId as number
      ),
    [poolContractAddress, walletAddress, chainId]
  );

  const isEnabled = !!walletAddress && !!poolContractAddress;
  const {
    data: contractData,
    isLoading,
    error,
    refetch,
  } = useReadContracts({
    contracts: contracts,
    query: {
      enabled: isEnabled,
      staleTime: 0,
      refetchInterval: 36_000,
    },
  });

  useEffect(() => {
    if (error) {
      logContractInteractionError(
        'Error reading staking user pool contract data',
        error,
        {
          poolContractAddress,
          walletAddress,
          chainId,
        }
      );
    }
  }, [error]);

  // Transform the contract data to match UserPoolInfo format
  const userPoolInfo: UserPoolInfo | undefined = useMemo(() => {
    return transformUserPoolInfoFromContracts(
      contractData as
        | { status: 'success' | 'failure'; result?: unknown }[]
        | undefined,
      walletAddress,
      poolContractAddress,
      pool
    );
  }, [contractData, walletAddress, poolContractAddress, pool]);

  // Return in a format compatible with useQuery for consistency
  // If query is disabled (no wallet), consider it as fetched (not loading)
  const isFetched = !isEnabled || (!isLoading && contractData !== undefined);

  return {
    data: userPoolInfo,
    isLoading: isEnabled ? isLoading : false,
    isFetched,
    error,
    refetch,
    isSuccess: !!userPoolInfo && !error,
    isError: !!error,
  };
};

type UseFirstDepositCalcInfoParams = QueryOptions & {
  poolContractAddress: `0x${string}`;
  walletAddress: `0x${string}`;
  chainId: NetworkChainIds;
  lockedPeriod: number;
  tokenAmount: string;
  pool?: StakingPool;
};

export const useStakingDepositCalcInfo = (
  options: UseFirstDepositCalcInfoParams
) => {
  const {
    poolContractAddress,
    walletAddress,
    chainId,
    lockedPeriod,
    tokenAmount,
    query,
    pool,
  } = options;

  const { data: userPoolInfo, isLoading: isUserLoading } =
    useStakingUserPoolInfo({
      poolContractAddress,
      walletAddress,
      chainId,
      pool: pool!,
    });

  const { data: walletRewardsInfo, isLoading: isWalletLoading } = useWalletSREP(
    {
      walletAddress,
      depositTokenSymbol: pool?.depositTokenSymbol,
      query: {
        enabled: !!pool?.depositTokenSymbol,
      },
    }
  );

  const { data: block } = useBlock({
    watch: true,
    query: {
      enabled: true,
    },
  });
  const blockTimestamp = block?.timestamp
    ? Number(block.timestamp)
    : Math.floor(Date.now() / 1000);

  const shouldCalculate =
    (query?.enabled ?? true) &&
    !!pool &&
    !!userPoolInfo &&
    !!walletRewardsInfo &&
    !!blockTimestamp;

  const calculatedData = useMemo(() => {
    if (!shouldCalculate) return undefined;

    return calculateStake(
      pool!,
      userPoolInfo!,
      walletRewardsInfo,
      tokenAmount,
      lockedPeriod,
      blockTimestamp
    );
  }, [
    shouldCalculate,
    pool,
    userPoolInfo,
    walletRewardsInfo,
    tokenAmount,
    lockedPeriod,
    blockTimestamp,
  ]);

  const isLoading =
    (query?.enabled ?? true) &&
    (isUserLoading || isWalletLoading || !pool || !blockTimestamp);

  return {
    data: calculatedData,
    isLoading: isLoading,
    isFetching: isLoading,
    isFetched: !!calculatedData || (!isLoading && !calculatedData),
    isSuccess: !!calculatedData,
    isError: false,
    error: null,
  };
};

type UseExtendCalcInfoParams = QueryOptions & {
  poolContractAddress: `0x${string}`;
  walletAddress: `0x${string}`;
  chainId: NetworkChainIds;
  lockedPeriod: number;
};

export const useStakingExtendCalcInfo = (options: UseExtendCalcInfoParams) => {
  const { poolContractAddress, walletAddress, chainId, lockedPeriod, query } =
    options;

  const [debouncedQueryKey] = useDebounce(
    [
      QueryKey.STAKING_EXTEND_CALC_INFO,
      chainId,
      poolContractAddress,
      walletAddress,
      lockedPeriod,
    ].join('-'),
    500
  );

  const useQueryOptions = {
    queryKey: [debouncedQueryKey],
    ...query,
  };

  return useQuery({
    ...useQueryOptions,
    queryFn: () =>
      stakingAPI.loadExtendCalcInfo({
        chainId,
        poolContractAddress,
        walletAddress,
        lockedPeriod,
      }),
  });
};

type UseWithdrawCalcInfoParams = QueryOptions & {
  poolContractAddress: `0x${string}`;
  walletAddress: `0x${string}`;
  chainId: NetworkChainIds;
  tokenAmount: string;
};

export const useStakingWithdrawCalcInfo = (
  options: UseWithdrawCalcInfoParams
) => {
  const { poolContractAddress, walletAddress, chainId, tokenAmount, query } =
    options;

  const [debouncedQueryKey] = useDebounce(
    [
      QueryKey.STAKING_WITHDRAW_CALC_INFO,
      chainId,
      poolContractAddress,
      walletAddress,
      tokenAmount,
    ].join('-'),
    500
  );

  const useQueryOptions = {
    queryKey: [debouncedQueryKey],
    enabled: true,
    ...query,
  };

  return useQuery({
    ...useQueryOptions,
    queryFn: () =>
      stakingAPI.loadWithdrawCalcInfo({
        chainId,
        poolContractAddress,
        walletAddress,
        tokenAmount,
      }),
  });
};

type UseStakingProjectInfo = QueryOptions & {
  tokenSymbol: string;
};

export const useStakingProjectInfo = (options: UseStakingProjectInfo) => {
  const { tokenSymbol, query } = options;
  const useQueryOptions = {
    queryKey: queryKeys.stakingProject({ tokenSymbol }),
    ...query,
  };

  return useQuery({
    ...useQueryOptions,
    queryFn: () =>
      stakingAPI.loadStakingProjectDetails({
        tokenSymbol: tokenSymbol,
      }),
  });
};

type UseWalletSREPParams = QueryOptions & {
  walletAddress: `0x${string}`;
  depositTokenSymbol?: string | null;
  placeholderData?: PlaceholderDataFunction<WalletRewardsInfo>;
};

export const useWalletSREP = (options: UseWalletSREPParams) => {
  const { walletAddress, query, depositTokenSymbol } = options;

  const useQueryOptions = {
    queryKey: queryKeys.srepWallet({
      walletAddress,
      depositTokenSymbol: depositTokenSymbol ?? '',
    }),
    staleTime: ETH_AVERAGE_BLOCK_TIME,
    ...query,
  };

  return useQuery({
    ...useQueryOptions,
    queryFn: () =>
      stakingAPI.loadWalletSREP({
        walletAddress,
        depositTokenSymbol,
      }),
  });
};

export const useUpdatePoolMetadata = () => {
  return useMutation({
    mutationFn: () => stakingAPI.updatePoolMetadata(),
  });
};

export type UserPosition = {
  pool: StakingPool;
  userInfo: UserPoolInfo;
};

type UseAllUserStakingPositionsParams = {
  walletAddress: `0x${string}`;
};

export const useAllUserStakingPositions = (
  options: UseAllUserStakingPositionsParams
) => {
  const { walletAddress } = options;

  const { data: pools, isLoading: isPoolsLoading } = useStakingPoolList();

  const { contracts, poolsWithCalls } = useMemo(() => {
    if (!pools || !walletAddress) return { contracts: [], poolsWithCalls: [] };

    const poolsWithCalls: StakingPool[] = [];
    const contracts = pools.flatMap((pool) => {
      if (!pool.stakingContractAddress) return [];
      poolsWithCalls.push(pool);
      return createUserPoolInfoContractCalls(
        pool.stakingContractAddress,
        walletAddress,
        pool.chainId as number
      );
    });

    return { contracts, poolsWithCalls };
  }, [pools, walletAddress]);

  const isEnabled = !!walletAddress && contracts.length > 0;

  const { data: contractData, isLoading: isContractLoading } = useReadContracts(
    {
      contracts,
      query: { enabled: isEnabled },
    }
  );

  const positions: UserPosition[] = useMemo(() => {
    if (!contractData || !poolsWithCalls.length) return [];

    return poolsWithCalls.map((pool, i) => {
      const poolContractData = contractData.slice(i * 2, i * 2 + 2);
      const userInfo = transformUserPoolInfoFromContracts(
        poolContractData as {
          status: 'success' | 'failure';
          result?: unknown;
        }[],
        walletAddress,
        pool.stakingContractAddress,
        pool
      );
      return { pool, userInfo };
    });
  }, [contractData, poolsWithCalls, walletAddress]);

  return {
    data: positions,
    isLoading: isPoolsLoading || (isEnabled && isContractLoading),
    isFetched: !isPoolsLoading && (!isEnabled || contractData !== undefined),
  };
};
